import { inject, Injectable, signal } from '@angular/core';
import { finalize, map, Observable, of, shareReplay, tap } from 'rxjs';

import { ObserverPhotoDto, ObserverPhotoViewModel } from '../models/observer-photo.model';
import { ObserverPhotoApiService } from './observer-photo-api.service';

@Injectable({
  providedIn: 'root',
})
export class ObserverPhotoStoreService {
  private readonly observerPhotoApi = inject(ObserverPhotoApiService);
  private readonly photoCache = new Map<string, ObserverPhotoViewModel>();
  private readonly requestCache = new Map<string, Observable<ObserverPhotoViewModel>>();

  readonly photosById = signal<Record<string, ObserverPhotoViewModel>>({});

  loadPhoto(photoId: string): Observable<ObserverPhotoViewModel> {
    const cachedPhoto = this.photoCache.get(photoId);

    if (cachedPhoto) {
      this.publishPhoto(cachedPhoto);
      return of(cachedPhoto);
    }

    const cachedRequest = this.requestCache.get(photoId);

    if (cachedRequest) {
      return cachedRequest;
    }

    const request = this.observerPhotoApi.getPhoto(photoId).pipe(
      map((photo) => this.toViewModel(photoId, photo)),
      tap((photo) => {
        this.photoCache.set(photo.photoId, photo);
        this.publishPhoto(photo);
      }),
      finalize(() => this.requestCache.delete(photoId)),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    this.requestCache.set(photoId, request);

    return request;
  }

  private publishPhoto(photo: ObserverPhotoViewModel): void {
    this.photosById.update((photosById) =>
      photosById[photo.photoId]
        ? photosById
        : {
            ...photosById,
            [photo.photoId]: photo,
          },
    );
  }

  private toViewModel(photoId: string, photo: ObserverPhotoDto): ObserverPhotoViewModel {
    const base64Data = photo.base64Data?.trim();

    if (!base64Data) {
      throw new Error(`Observer photo ${photoId} did not include base64Data.`);
    }

    const contentType = photo.contentType?.trim() || photo.mimeType?.trim() || 'image/jpeg';

    return {
      photoId,
      deviceId: photo.deviceId,
      uploadedAtUtc: photo.uploadedAtUtc,
      endTimeUtc: photo.endTimeUtc,
      base64Data,
      imageUrl: base64Data.startsWith('data:') ? base64Data : `data:${contentType};base64,${base64Data}`,
      contentType,
      fileName: photo.fileName ?? null,
    };
  }
}
